import * as cookieStorage from "../redux/actions/common/cookieStorage";
import utils from "./utils";

const cStorage = cookieStorage.getCookieStorage();

/**
 * Gets the visitor ID from cookie storage, or generates and persists a new one.
 * This is the single source of truth for the anonymous visitor ID used by
 * all homepage analytics events.
 *
 * Exported so SteamSignInButton and App.js can append it to auth requests
 * without going through the track() function.
 */
export function getOrCreateVisitorId() {
  const stats = cStorage.getObject("stats") || {};
  if (stats.visitorId && typeof stats.visitorId === "string") {
    return stats.visitorId;
  }

  // Generate a new visitor ID
  let newId;
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    newId = crypto.randomUUID();
  } else {
    newId = Date.now().toString(36) + Math.random().toString(36).slice(2);
  }

  // Persist back into stats object without clobbering existing keys
  cStorage.setObject("stats", { ...stats, visitorId: newId });
  return newId;
}

const TRACK_ENDPOINT =
  typeof utils.getEdgeBasePath === "function"
    ? `${utils.getEdgeBasePath()}/public/track`
    : "/edge/public/track";

/**
 * Fire-and-forget analytics event tracking.
 *
 * Never throws. Never awaits from call sites.
 * Uses sendBeacon when available (survives page unload); falls back to fetch.
 */
export function track(event, payload = {}) {
  try {
    const visitorId = getOrCreateVisitorId();
    const body = JSON.stringify({ event, visitorId, ...payload });

    if (
      typeof navigator !== "undefined" &&
      typeof navigator.sendBeacon === "function" &&
      body.length < 60_000
    ) {
      // sendBeacon is best-effort; if it returns false, fall through to fetch.
      const blob = new Blob([body], { type: "application/json" });
      const sent = navigator.sendBeacon(TRACK_ENDPOINT, blob);
      if (sent) return;
    }

    // Fallback: fetch with keepalive so the request survives page navigation.
    fetch(TRACK_ENDPOINT, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body,
    }).catch(() => {});
  } catch (_) {
    // Swallow all errors — tracking must never crash a component.
  }
}
